import type { MailingAddress, Metafield } from "@shopify/hydrogen-react/storefront-api-types";

export interface IFetchReturnOrder {
  data: {
    return: IReturn;
  };
}

export const fetchReturnOrder = async ({
  storeName,
  domain = "",
  returnId,
  customerId,
  returnRefundLineItems = [],
  refundShippingFee,
  refundDuties = [],
}: {
  storeName: string;
  domain?: "kr" | "intl" | "";
  returnId: string;
  customerId: string;
  returnRefundLineItems: IReturnRefundLineItem[];
  refundShippingFee: number;
  refundDuties?: IRefundDuty[];
}): Promise<IFetchReturnOrder | undefined> => {
  const lambdaUrl = "https://e3xxcumqzbbcovj2twqhkamrei0fvnvk.lambda-url.ap-northeast-2.on.aws/"; // common_get_return_order_v2

  try {
    const response = await fetch(lambdaUrl, {
      method: "POST",
      body: JSON.stringify({
        returnId,
        storeName,
        domain,
        customerId,
        returnRefundLineItems,
        refundDuties,
        refundShippingFee,
      }),
    }).then((res) => res);

    if (response.status === 200) {
      return (await response.json()) as IFetchReturnOrder;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(message);
  }
};

interface IAmountSet {
  shopMoney: {
    amount: string;
  };
}

export interface IReturnLineItem {
  fulfillmentLineItem: {
    quantity: number;
    discountedTotalSet: IAmountSet;
    originalTotalSet: IAmountSet;
    lineItem: {
      name: string;
      vendor: string;
      id: string;
      image: {
        url: string;
        height: number;
        width: number;
      };
      product: {
        id: string;
        thumbnailImages?: Metafield;
      };
      title: string;
      variantTitle: string;
      variant: {
        selectedOptions: {
          name: string;
          value: string;
        }[];
      };
      discountAllocations: IDiscountAllocation[];
    };
  };
  customerNote: string;
  id: string;
  quantity: number;
  refundedQuantity: number;
  returnReason: string;
}

export interface IReturn {
  suggestedRefund: {
    amount: IAmountSet;
    shipping: {
      amountSet: IAmountSet;
    };
    subtotal: IAmountSet;
    discountedSubtotal: IAmountSet;
    totalTax: IAmountSet;
    totalDuties: IAmountSet;
  };
  status: string;
  name: string;
  tags: string[];
  customAttributes: { value: string; key: string }[];
  order: {
    id: string;
    name: string;
    customer: {
      id: string;
    };
    shippingAddress: MailingAddress;
    billingAddress: MailingAddress;
    customAttributes?: { key: string; value: string }[];
    tags?: string[];
  };
  totalQuantity: number;
  returnLineItems: {
    edges: { node: IReturnLineItem }[];
  };
}

interface IReturnRefundLineItem {
  returnLineItemId: string;
  quantity: number;
}

interface IRefundDuty {
  dutyId: string;
  refundType: string;
}

export interface IDiscountAllocation {
  allocatedAmount: IShopMoney;
}

interface IShopMoney {
  amount: string;
}
